import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, MessageCircle, AlertTriangle, ShieldCheck, CheckCircle2, BellRing, DollarSign, X } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SLA_BADGES = {
  active:      { label: 'ACTIVE',     bg: '#1A7F4F', fg: '#FFFFFF' },
  waiting:     { label: 'WAITING',    bg: '#E8A830', fg: '#0A1F2E' },
  sla_breach:  { label: 'SLA BREACH', bg: '#C0392B', fg: '#FFFFFF' },
  escalated:   { label: 'ESCALATED',  bg: '#7B1212', fg: '#FFFFFF' },
  resolved:    { label: 'RESOLVED',   bg: '#5A6B73', fg: '#FFFFFF' },
  flagged:     { label: 'FLAGGED',    bg: '#C0392B', fg: '#FFFFFF' },
};

const FILTER_TABS = [
  { id: 'all',        label: 'All' },
  { id: 'active',     label: 'Active' },
  { id: 'waiting',    label: 'Waiting' },
  { id: 'sla_breach', label: 'SLA Breach' },
  { id: 'escalated',  label: 'Escalated' },
  { id: 'resolved',   label: 'Resolved' },
];

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch { return iso; }
};
const fmtRelative = (iso) => {
  if (!iso) return '-';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return iso; }
};
const fmtMxCity = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/Mexico_City',
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) + ' CDT';
  } catch { return iso; }
};

const SlaPill = ({ badge }) => {
  const cfg = SLA_BADGES[badge] || SLA_BADGES.active;
  return (
    <span
      className="inline-block px-2 py-0.5 font-mono uppercase text-[9px] tracking-[0.18em] font-semibold rounded"
      style={{ background: cfg.bg, color: cfg.fg }}
      data-testid={`sla-pill-${badge}`}
    >
      {cfg.label}
    </span>
  );
};

// =========================================================================
// Detail side panel - read-only thread + actions
// =========================================================================
const ConversationDetail = ({ conversationId, onClose, onRefresh }) => {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/partner-conversations/${conversationId}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) { setThread(null); return; }
      setThread(await r.json());
    } finally {
      setLoading(false);
    }
  }, [conversationId]);
  useEffect(() => { if (conversationId) load(); }, [conversationId, load]);

  const post = async (path) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/partner-conversations/${conversationId}${path}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type':'application/json', ...authHeaders() },
        body: '{}',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) {
        toast.error(d.detail || 'Action failed.');
        return null;
      }
      await load();
      onRefresh?.();
      return d;
    } finally {
      setBusy(false);
    }
  };

  const nudge = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/partner-conversations/${conversationId}/nudge`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type':'application/json', ...authHeaders() },
        body: '{}',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) {
        toast.error(d.detail || 'Nudge failed.');
        return;
      }
      toast.success(
        d.escalation_fired
          ? `Nudge sent - partner is now escalated (3rd nudge). Founders alerted.`
          : `Nudge sent. nudge_count=${d.nudge_count}`,
      );
      try {
        trackEvent('partner_nudge_sent', {
          partner_id: thread?.partner?.id,
          conversation_id: conversationId,
          nudge_count: d.nudge_count,
        });
      } catch (_) { /* noop */ }
      await load();
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/partner-conversations/${conversationId}/resolve`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type':'application/json', ...authHeaders() },
        body: JSON.stringify({ resolution_note: resolveNote }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) { toast.error(d.detail || 'Resolve failed.'); return; }
      toast.success('Conversation resolved.');
      setResolveOpen(false);
      setResolveNote('');
      await load();
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  if (!conversationId) return null;
  const conv = thread?.conversation || {};
  const partner = thread?.partner || {};
  const member = thread?.member || {};
  const slaBadge = thread?.sla_badge || conv.status || 'active';
  const nudgeDisabled =
    busy ||
    (conv.nudge_count || 0) >= 3 ||
    (conv.last_nudge_at && (Date.now() - new Date(conv.last_nudge_at).getTime()) < 24 * 3600 * 1000) ||
    conv.status === 'resolved';
  let nudgeLabel = 'Send a Nudge';
  if ((conv.nudge_count || 0) >= 3) nudgeLabel = 'Escalated - review manually';
  else if (conv.last_nudge_at && (Date.now() - new Date(conv.last_nudge_at).getTime()) < 24 * 3600 * 1000) {
    nudgeLabel = `Cooldown · last nudge ${fmtRelative(conv.last_nudge_at)}`;
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-conv-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl h-full bg-[#F9F6EF] overflow-y-auto shadow-2xl">
        <header className="sticky top-0 bg-[#F9F6EF] z-10 px-6 py-4 border-b border-[#EFE6CF] flex items-center justify-between">
          <div className="min-w-0">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#2CA8A0]">Conversation</p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E] truncate">
              {partner.name || 'Conversation'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <SlaPill badge={slaBadge} />
            <button onClick={onClose} className="p-2 text-[#5A6B73] hover:text-[#0A1F2E]" data-testid="admin-conv-detail-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" /></div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <dl className="grid grid-cols-2 gap-3 text-sm bg-white border border-[#EFE6CF] rounded-xl p-4">
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Subject</dt><dd className="text-[#0A1F2E]">{conv.subject}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Status</dt><dd className="text-[#0A1F2E]">{conv.status}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Member</dt><dd className="text-[#0A1F2E]">{member.first_name} <span className="text-[#5A6B73] text-xs">&lt;{member.email}&gt;</span></dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Messages</dt><dd className="text-[#0A1F2E]">{conv.message_count}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Nudges</dt><dd className="text-[#0A1F2E]" data-testid="admin-conv-nudge-count">{conv.nudge_count || 0}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Hours overdue</dt><dd className="text-[#0A1F2E]">{thread.hours_overdue}h</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Last nudge</dt><dd className="text-[#0A1F2E]">{conv.last_nudge_at ? fmtMxCity(conv.last_nudge_at) : '-'}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Billed</dt><dd className="text-[#0A1F2E]">{conv.introduction_fee_charged ? `Yes · ${fmtMxCity(conv.billed_at)}` : 'No'}</dd></div>
            </dl>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={nudge}
                disabled={nudgeDisabled}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.16em] font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-conv-nudge-btn"
                title={nudgeLabel}
              >
                <BellRing className="w-3.5 h-3.5" /> {nudgeLabel}
              </button>
              <button
                type="button"
                onClick={() => setResolveOpen(true)}
                disabled={busy || conv.status === 'resolved'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.16em] font-semibold rounded-full disabled:opacity-50"
                data-testid="admin-conv-resolve-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
              </button>
              <button
                type="button"
                onClick={() => post('/mark-billed')}
                disabled={busy || conv.introduction_fee_charged}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#1A7F4F]/35 hover:border-[#1A7F4F]/55 text-[#1A7F4F] font-mono uppercase text-[10px] tracking-[0.16em] font-semibold rounded-full disabled:opacity-50"
                data-testid="admin-conv-mark-billed-btn"
              >
                <DollarSign className="w-3.5 h-3.5" /> {conv.introduction_fee_charged ? 'Billed' : 'Mark billed'}
              </button>
            </div>

            {resolveOpen && (
              <div className="bg-white border border-[#EFE6CF] rounded-xl p-4">
                <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#5A6B73] mb-2">Resolution note (optional)</p>
                <textarea
                  rows={3}
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value.slice(0, 1000))}
                  className="w-full px-3 py-2 bg-white border border-[#C4B9A0] text-[#0A1F2E] rounded text-sm focus:outline-none focus:border-[#2CA8A0]"
                  data-testid="admin-conv-resolve-note"
                />
                <div className="flex gap-2 mt-2">
                  <button onClick={resolve} disabled={busy} className="px-4 py-2 bg-[#0A1F2E] text-white font-mono uppercase text-[10px] tracking-[0.16em] rounded-full" data-testid="admin-conv-resolve-confirm">Confirm</button>
                  <button onClick={() => setResolveOpen(false)} className="px-4 py-2 bg-white border border-[#0A1F2E]/20 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.16em] rounded-full">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-2" data-testid="admin-conv-thread">
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">Conversation thread (read-only)</p>
              {(thread.messages || []).map((m) => {
                const isMember = m.sender_type === 'member';
                return (
                  <div key={m.message_id} className={`flex ${isMember ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[78%] px-4 py-3 rounded-xl text-sm whitespace-pre-wrap ${
                      isMember ? 'bg-[#F4ECD8] text-[#0A1F2E]' : 'bg-white border border-[#EFE6CF] text-[#0A1F2E]'
                    }`}>
                      <p className="font-mono uppercase text-[9px] tracking-[0.16em] mb-1 text-[#5A6B73]">
                        {isMember ? member.first_name || 'Member' : partner.name} · {fmtMxCity(m.sent_at)}
                      </p>
                      {m.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

const SummaryCard = ({ label, value, tone = 'default', testId }) => {
  const toneCls = tone === 'red' ? 'text-[#C0392B]' : tone === 'amber' ? 'text-[#E8A830]' : 'text-[#0A1F2E]';
  return (
    <div className="bg-white border border-[#EFE6CF] rounded-xl p-4" data-testid={testId}>
      <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#5A6B73] mb-1">{label}</p>
      <p className={`font-display italic text-3xl ${toneCls}`}>{value ?? '-'}</p>
    </div>
  );
};

// =========================================================================
// Main page
// =========================================================================
const AdminPartnerConversations = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/partner-conversations`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        setRows(d.conversations || []);
      }
      const h = await fetch(`${API}/api/admin/partner-conversations-health`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (h.ok) setHealth(await h.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => r.sla_badge === filter || r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.sla_badge === 'active').length,
    waiting: rows.filter((r) => r.sla_badge === 'waiting').length,
    sla_breach: rows.filter((r) => r.sla_badge === 'sla_breach').length,
    escalated: rows.filter((r) => r.sla_badge === 'escalated').length,
    resolved: rows.filter((r) => r.status === 'resolved').length,
    unbilled: rows.filter((r) => !r.introduction_fee_charged).length,
    flagged: rows.filter((r) => r.status === 'flagged').length,
  }), [rows]);

  return (
    <div className="min-h-screen bg-[#F9F6EF]" data-testid="admin-partner-conversations-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">
              Partners · Conversations
            </p>
            <h1 className="font-display italic text-4xl text-[#0A1F2E] mb-2">Partner Conversations</h1>
            <p className="text-[#5A6B73] text-sm">
              Every member↔partner conversation logged for quality assurance.
            </p>
          </div>
        </header>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="admin-conv-summary">
          <SummaryCard testId="card-total-month" label="This Month" value={health?.total_conversations_this_week ?? rows.length} />
          <SummaryCard testId="card-avg-response" label="Avg Response (h)" value={health?.avg_response_hours_this_week ?? '-'} />
          <SummaryCard testId="card-sla-breaches" label="SLA Breaches" tone="red" value={counts.sla_breach} />
          <SummaryCard testId="card-escalated" label="Escalated" tone="red" value={counts.escalated} />
          <SummaryCard testId="card-flagged" label="Flagged" tone="amber" value={counts.flagged} />
          <SummaryCard testId="card-unbilled" label="Unbilled" tone="amber" value={counts.unbilled} />
        </div>

        {/* Filter chips */}
        <div className="inline-flex items-center bg-white border border-[#EFE6CF] rounded-full p-1 mb-5" data-testid="admin-conv-filter-tabs">
          {FILTER_TABS.map((t) => {
            const active = filter === t.id;
            const n = counts[t.id] ?? 0;
            return (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className={`px-4 py-1.5 font-mono uppercase text-[10px] tracking-[0.16em] font-semibold rounded-full transition ${
                  active ? 'bg-[#0A1F2E] text-white' : 'text-[#5A6B73] hover:text-[#0A1F2E]'
                }`}
                data-testid={`admin-conv-filter-${t.id}`}
              >
                {t.label} <span className="opacity-60 ml-1">{n}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" /></div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#EFE6CF] rounded-2xl" data-testid="admin-conv-empty">
            <MessageCircle className="w-10 h-10 text-[#5A6B73] mx-auto mb-2" />
            <p className="text-[#5A6B73]">No conversations match this filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl">
            <table className="w-full text-sm" data-testid="admin-conv-table">
              <thead className="bg-[#F9F6EF] border-b border-[#EFE6CF]">
                <tr>
                  {['Date', 'Partner', 'Member', 'Subject', 'Msgs', 'Last activity', 'SLA', 'Action'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-mono uppercase text-[9px] tracking-[0.16em] text-[#5A6B73]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.conversation_id} className="border-t border-[#EFE6CF] hover:bg-[#F9F6EF]/40" data-testid={`admin-conv-row-${c.conversation_id}`}>
                    <td className="px-3 py-2 text-[#0A1F2E] whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2 text-[#0A1F2E]">{c.partner_name}</td>
                    <td className="px-3 py-2 text-[#0A1F2E]" data-testid={`admin-conv-member-${c.conversation_id}`}>{c.member_first_name}</td>
                    <td className="px-3 py-2 text-[#0A1F2E] max-w-[260px] truncate">{c.subject}</td>
                    <td className="px-3 py-2 text-[#0A1F2E]">{c.message_count}</td>
                    <td className="px-3 py-2 text-[#5A6B73] whitespace-nowrap">{fmtRelative(c.last_message_at)}</td>
                    <td className="px-3 py-2"><SlaPill badge={c.sla_badge} /></td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setOpenId(c.conversation_id)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-[#0A1F2E] text-white font-mono uppercase text-[9px] tracking-[0.16em] rounded-full"
                        data-testid={`admin-conv-view-${c.conversation_id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId && (
        <ConversationDetail
          conversationId={openId}
          onClose={() => setOpenId(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
};

export default AdminPartnerConversations;
